/**
 * Item Variations Tab Widget
 * 
 * Shows all variant items for a template product in a paginated table format.
 * Similar to Shopify's variant management interface.
 * 
 * Features:
 * - Paginated table view of all variants
 * - Filter by attribute group
 * - Group variations by attribute group
 * - Click to navigate to variant detail page
 * - Shows key variant information (item_code, item_name, status, price, attributes)
 * - Only renders for template items (has_variants === 1 && !variant_of)
 * - Server-side pagination and search
 * - Client-side grouping and filtering by attribute
 * 
 * Architecture:
 * - Uses useInventoryListWithPagination for data fetching
 * - Filters by variant_of = template item_code
 * - Fetches attributes data separately or includes in fields
 * - Client-side grouping and filtering for better UX
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueries } from '@tanstack/react-query';
import { erp } from '@/lib/erp';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Archive, Package, Search, Filter, ChevronDown, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { CARD } from "@/config/design-tokens";
import type { ShopifySectionWidgetProps } from '../types';
import { useInventoryListWithPagination } from '@/features/inventory/api/inventoryHooks';
import { frappeMethod } from '@/lib/erp/utils';
import { formatCurrency } from '@/lib/format/currency';
import { useDebounce } from '@/lib/hooks/useDebounce';
import { useMetaDrivenListSettings } from '@/lib/hooks/useMetaDrivenListSettings';
import { ListSettingsButton } from '@/features/common/components/ListSettingsButton';
import { ListSettingsModal } from '@/features/common/components/ListSettingsModal';
import { VariantRowActions } from './VariantRowActions';
import { buildDocRoute } from '@/lib/config/docRoutes';
import type { FilterDescriptor } from '@/lib/erp/filterTypes';

interface VariantAttribute {
  attribute: string;
  attribute_value: string;
}

interface VariantWithAttributes {
  name: string;
  item_code: string;
  item_name?: string;
  disabled?: 0 | 1;
  standard_rate?: number;
  stock_uom?: string;
  attributes?: VariantAttribute[] | string; // Can be array or JSON string
  [key: string]: any;
}

type GroupByMode = 'none' | 'attribute';
type ViewMode = 'table' | 'grouped';

// NOTE: This widget uses fieldname-driven rendering (not TanStack Table)
// because grouped view requires stable row references, and TanStack's
// getRowModel().rows creates new arrays per render, causing freeze issues.

const LIST_SETTINGS_KEY = "inventory.item_variations";

const FALLBACK_COLUMNS = ["item_code", "item_name", "disabled", "standard_rate"];
const NON_API_FIELDS = new Set(["attributes_display", "__actions"]);

export function ItemVariationsTabWidget({
  formData,
}: ShopifySectionWidgetProps) {
  const navigate = useNavigate();
  const itemCode = formData.item_code;
  const hasVariants = formData.has_variants === 1 || formData.has_variants === true;
  // Frappe returns "" for unset Link fields — normalize to null
  const variantOf = typeof formData.variant_of === "string" && formData.variant_of.trim()
    ? formData.variant_of
    : null;

  // Only show for template items (not variants themselves)
  const isTemplate = hasVariants && !variantOf;
  
  // List settings configuration
  const listSettingsConfig = useMemo(() => ({
    key: LIST_SETTINGS_KEY,
    meta: {
      doctype: "Item",
      includeFieldtypes: ["Data", "Link", "Select", "Check", "Currency", "Int", "Float", "Date", "Datetime"],
      excludeFieldtypes: ["Table", "Section Break", "Column Break", "Tab Break", "HTML", "Button"],
      excludeFieldnames: ["variant_of", "has_variants", "attributes", "naming_series", "image"],
      requiredFieldnames: ["item_code"],
      defaultVisibleFieldnames: ["item_code", "item_name", "disabled", "standard_rate"],
    },
    staticFields: [
      { fieldname: "attributes_display", label: "Attributes", fieldtype: "Data", required: false },
    ],
  }), []);
  
  // List settings hook (must be called unconditionally, before any early return)
  const listSettings = useMetaDrivenListSettings({ config: listSettingsConfig });
  const [isListSettingsModalOpen, setIsListSettingsModalOpen] = useState(false);
  
  // Archive toggle state
  const [showArchivedVariants, setShowArchivedVariants] = useState(false);

  // Archived variant count — lightweight server query (separate from list data)
  const { data: archivedCountData } = useQuery({
    queryKey: ['variant-archived-count', itemCode],
    queryFn: () =>
      frappeMethod<number>('frappe.client.get_count', {
        doctype: 'Item',
        filters: { variant_of: itemCode, disabled: 1 },
      }),
    enabled: !!itemCode && isTemplate,
    staleTime: 60_000,
  });
  const archivedVariantCount = archivedCountData ?? 0;

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Filter state
  const [selectedAttributeGroup, setSelectedAttributeGroup] = useState<string>('all');
  const [groupByMode, setGroupByMode] = useState<GroupByMode>('none');
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  
  // Pagination state
  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = 50; // Increased for better grouping experience
  
  // Build field meta map for column rendering
  const fieldMetaByName = useMemo(() => {
    const map = new Map<string, { label: string; fieldtype: string }>();
    listSettings.availableFields.forEach(f => {
      const fieldtype = ("fieldtype" in f ? (f as any).fieldtype : undefined) as string | undefined;
      map.set(f.fieldname, { 
        label: f.label, 
        fieldtype: fieldtype || "Data" 
      });
    });
    return map;
  }, [listSettings.availableFields]);
  
  // Compute visible column fieldnames (with loading fallback)
  const visibleColumnFieldnames = useMemo(() => {
    if (listSettings.isLoading || !listSettings.visibleFieldOrder?.length) {
      return FALLBACK_COLUMNS;
    }
    return listSettings.visibleFieldOrder;
  }, [listSettings.isLoading, listSettings.visibleFieldOrder]);
  
  // Compute effective fields for API (exclude synthetic/UI-only fields)
  const effectiveFields = useMemo(() => {
    const apiFields = visibleColumnFieldnames.filter(f => !NON_API_FIELDS.has(f));
    // Always include "disabled" for archived row styling
    return Array.from(new Set(["name", "item_code", "stock_uom", "disabled", ...apiFields]));
  }, [visibleColumnFieldnames]);

  // Build filter descriptors — hide archived variants by default
  const variantFilterDescriptors = useMemo((): FilterDescriptor[] => {
    const filters: FilterDescriptor[] = [
      { field: 'variant_of', operator: 'equals' as const, value: itemCode },
    ];
    if (!showArchivedVariants) {
      filters.push({ field: 'disabled', operator: 'equals' as const, value: '0' });
    }
    return filters;
  }, [itemCode, showArchivedVariants]);

  // Fetch variants with pagination
  // NOTE: Attributes are stored in a child table "Item Attribute" in ERPNext.
  // List endpoints don't return child table data, so we fetch attributes separately.
  const { data, isLoading, error } = useInventoryListWithPagination({
    filterDescriptors: variantFilterDescriptors,
    searchQuery: debouncedSearch || undefined,
    pageIndex,
    pageSize,
    orderBy: 'item_code asc',
    fields: effectiveFields,
  });

  // Fetch attributes separately for each variant (child table data not in list response)
  // CRITICAL: Always use stable array reference to prevent hooks violation
  // When variants is empty, useQueries still needs to be called with empty array
  const variants = (data?.data || []) as VariantWithAttributes[];
  const attributeQueries = useQueries({
    queries: variants.length > 0 
      ? variants.map((variant) => ({
          queryKey: ['item-detail', variant.item_code],
          queryFn: () => erp.items.detail(variant.item_code),
          enabled: !!variant.item_code,
          staleTime: 60_000, // Cache for 1 minute
        }))
      : [], // Always provide array, even if empty
  });

  // Map item_code -> attributes for quick lookup
  const attributesByItemCode = useMemo(() => {
    const map = new Map<string, VariantAttribute[]>();
    attributeQueries.forEach((query, index) => {
      const variant = variants[index];
      if (variant && query.data) {
        const itemDetail = query.data as any;
        // Attributes are in the child table, typically as an array
        if (Array.isArray(itemDetail.attributes)) {
          map.set(variant.item_code, itemDetail.attributes);
        }
      }
    });
    return map;
  }, [attributeQueries, variants]);

  // Merge attributes from detail queries into variants
  const variantsWithAttributes = useMemo(() => {
    return variants.map(variant => {
      // Get attributes from the detail query result
      const attributes = attributesByItemCode.get(variant.item_code) || [];
      
      return {
        ...variant,
        parsedAttributes: attributes,
      };
    });
  }, [variants, attributesByItemCode]);

  // Extract unique attribute groups (attribute names)
  const attributeGroups = useMemo(() => {
    const groups = new Set<string>();
    variantsWithAttributes.forEach(variant => {
      variant.parsedAttributes?.forEach(attr => {
        if (attr.attribute) {
          groups.add(attr.attribute);
        }
      });
    });
    return Array.from(groups).sort();
  }, [variantsWithAttributes]);

  // Filter variants by selected attribute group
  const filteredVariants = useMemo(() => {
    if (selectedAttributeGroup === 'all') {
      return variantsWithAttributes;
    }
    
    return variantsWithAttributes.filter(variant => 
      variant.parsedAttributes?.some(attr => attr.attribute === selectedAttributeGroup)
    );
  }, [variantsWithAttributes, selectedAttributeGroup]);

  // Group variants by attribute
  const groupedVariants = useMemo(() => {
    if (groupByMode === 'none') {
      return { 'All Variants': filteredVariants };
    }
    
    if (groupByMode === 'attribute' && selectedAttributeGroup !== 'all') {
      const groups: Record<string, VariantWithAttributes[]> = {};
      
      filteredVariants.forEach(variant => {
        const attr = variant.parsedAttributes?.find(a => a.attribute === selectedAttributeGroup);
        const groupKey = attr?.attribute_value || 'Unspecified';
        
        if (!groups[groupKey]) {
          groups[groupKey] = [];
        }
        groups[groupKey].push(variant);
      });
      
      return groups;
    }
    
    // Group by first attribute if no specific group selected
    const groups: Record<string, VariantWithAttributes[]> = {};
    
    filteredVariants.forEach(variant => {
      const firstAttr = variant.parsedAttributes?.[0];
      if (firstAttr) {
        const groupKey = `${firstAttr.attribute}: ${firstAttr.attribute_value}`;
        if (!groups[groupKey]) {
          groups[groupKey] = [];
        }
        groups[groupKey].push(variant);
      } else {
        if (!groups['No Attributes']) {
          groups['No Attributes'] = [];
        }
        groups['No Attributes'].push(variant);
      }
    });
    
    return groups;
  }, [filteredVariants, groupByMode, selectedAttributeGroup]);

  // Toggle group expansion
  const toggleGroup = (groupKey: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  const total = data?.total || 0;
  const totalPages = Math.ceil(total / pageSize);
  const displayCount = filteredVariants.length;
  
  // Check if attributes are still loading
  const attributesLoading = attributeQueries.some(q => q.isLoading);
  const isLoadingAttributes = !isLoading && variants.length > 0 && attributesLoading;
  
  // Render cell based on fieldname (fieldname-driven rendering)
  const renderCell = useCallback((fieldname: string, variant: VariantWithAttributes): React.ReactNode => {
    const meta = fieldMetaByName.get(fieldname);
    const value = (variant as any)[fieldname];
    
    // Bespoke columns
    if (fieldname === "item_code") {
      return <span className="font-medium">{value}</span>;
    }
    if (fieldname === "item_name") {
      return value || "-";
    }
    if (fieldname === "disabled") {
      return (
        <Badge
          variant={value === 1 ? "outline" : "default"}
          className="text-xs"
        >
          {value === 1 ? "Archived" : "Active"}
        </Badge>
      );
    }
    if (fieldname === "standard_rate") {
      return value ? formatCurrency(value, "th-TH", "THB") : "-";
    }
    if (fieldname === "attributes_display") {
      const attrs = variant.parsedAttributes;
      if (!attrs?.length) {
        return <span className="text-muted-foreground text-xs">-</span>;
      }
      return (
        <div className="flex flex-wrap gap-1 max-w-[200px]">
          {attrs.slice(0, 2).map((a: VariantAttribute, i: number) => (
            <Badge key={i} variant="outline" className="text-xs">
              {a.attribute_value}
            </Badge>
          ))}
          {attrs.length > 2 && (
            <Badge variant="outline" className="text-xs">
              +{attrs.length - 2}
            </Badge>
          )}
        </div>
      );
    }
    
    // Generic rendering based on fieldtype
    if (value == null || value === "") {
      return <span className="text-muted-foreground">-</span>;
    }
    if (meta?.fieldtype === "Check") {
      return (
        <Badge variant={value ? "default" : "secondary"}>
          {value ? "Yes" : "No"}
        </Badge>
      );
    }
    if (meta?.fieldtype === "Date" || meta?.fieldtype === "Datetime") {
      try {
        return <div>{new Date(value).toLocaleDateString()}</div>;
      } catch {
        return <div>{String(value)}</div>;
      }
    }
    if (meta?.fieldtype === "Currency" || meta?.fieldtype === "Float" || meta?.fieldtype === "Int") {
      const n = Number(value);
      return isNaN(n) ? String(value) : n.toLocaleString();
    }
    return <span className="truncate max-w-[200px]">{String(value)}</span>;
  }, [fieldMetaByName]);

  // Don't render for non-template items
  // IMPORTANT: This must be AFTER all hooks (including renderCell/useCallback),
  // otherwise React will throw "Rendered fewer hooks than expected".
  if (!isTemplate) {
    return null;
  }

  if (error) {
    return (
      <Card className={CARD}>
        <CardContent className="p-8 sm:p-10">
          <div className="flex flex-col gap-4">
            <div className="text-sm text-destructive">
              Error loading variations: {error instanceof Error ? error.message : 'Unknown error'}
            </div>
            {listSettings && (
              <Button
                variant="outline"
                onClick={() => listSettings.resetToDefaults()}
              >
                Reset Columns
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={CARD}>
      <CardContent className="p-8 sm:p-10">
        <div className="flex flex-col space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-foreground">Product Variations</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {isLoading ? 'Loading...' : isLoadingAttributes ? 'Loading attributes...' : (
                  <>
                    {displayCount} of {total} {total === 1 ? 'variation' : 'variations'}
                    {selectedAttributeGroup !== 'all' && ` (filtered)`}
                  </>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowArchivedVariants(!showArchivedVariants)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs border transition-colors",
                  showArchivedVariants
                    ? "bg-muted text-foreground border-border"
                    : "bg-transparent text-muted-foreground border-border",
                )}
                aria-pressed={showArchivedVariants}
              >
                <Archive className="size-3" />
                Show archived
                {archivedVariantCount > 0 && (
                  <span className="text-muted-foreground">({archivedVariantCount})</span>
                )}
              </button>
              {listSettings && (
                <ListSettingsButton
                  onClick={() => setIsListSettingsModalOpen(true)}
                  disabled={listSettings.isLoading}
                />
              )}
            </div>
          </div>

          {/* Filters and Controls */}
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search variations by code or name..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPageIndex(0);
                }}
                className="pl-9"
              />
            </div>

            {/* Attribute Group Filter */}
            {attributeGroups.length > 0 && (
              <Select
                value={selectedAttributeGroup}
                onValueChange={(value) => {
                  setSelectedAttributeGroup(value);
                  setPageIndex(0);
                }}
              >
                <SelectTrigger className="w-full sm:w-[200px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by attribute" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Attributes</SelectItem>
                  {attributeGroups.map(group => (
                    <SelectItem key={group} value={group}>
                      {group}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Group By Toggle */}
            {attributeGroups.length > 0 && (
              <Select
                value={groupByMode}
                onValueChange={(value) => {
                  setGroupByMode(value as GroupByMode);
                  if (value === 'attribute') {
                    setViewMode('grouped');
                  } else {
                    setViewMode('table');
                  }
                }}
              >
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Grouping</SelectItem>
                  <SelectItem value="attribute">Group by Attribute</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Variations Table */}
          {isLoading && filteredVariants.length === 0 ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : filteredVariants.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Package className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-sm font-medium text-foreground mb-2">
                {debouncedSearch || selectedAttributeGroup !== 'all' 
                  ? 'No variations found' 
                  : 'No variations found'}
              </p>
              <p className="text-xs text-muted-foreground text-center max-w-sm">
                {debouncedSearch || selectedAttributeGroup !== 'all'
                  ? 'Try adjusting your search or filter terms.'
                  : 'This template product doesn\'t have any variants yet.'}
              </p>
            </div>
          ) : (
            <>
              <div className="border border-border/20 rounded overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {visibleColumnFieldnames.map(fieldname => (
                        <TableHead key={fieldname}>
                          {fieldMetaByName.get(fieldname)?.label || fieldname}
                        </TableHead>
                      ))}
                      <TableHead className="w-[100px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {viewMode === 'grouped' && groupByMode !== 'none' ? (
                      // Grouped view
                      Object.entries(groupedVariants).map(([groupKey, groupVariants]) => {
                        const isExpanded = expandedGroups.has(groupKey);
                        const groupCount = groupVariants.length;
                        
                        return (
                          <React.Fragment key={groupKey}>
                            {/* Group Header */}
                            <TableRow className="bg-muted/30 hover:bg-muted/40">
                              <TableCell colSpan={visibleColumnFieldnames.length + 1} className="p-0">
                                <button
                                  onClick={() => toggleGroup(groupKey)}
                                  className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
                                >
                                  <div className="flex items-center gap-2">
                                    {isExpanded ? (
                                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                    )}
                                    <span className="font-medium text-sm">{groupKey}</span>
                                    <Badge variant="secondary" className="text-xs">
                                      {groupCount} {groupCount === 1 ? 'variant' : 'variants'}
                                    </Badge>
                                  </div>
                                </button>
                              </TableCell>
                            </TableRow>
                            {/* Group Variants */}
                            {isExpanded && groupVariants.map(variant => {
                              const variantRoute = buildDocRoute('Item', variant.item_code);
                              return (
                                <TableRow
                                  key={variant.name}
                                  className={cn(
                                    "cursor-pointer hover:bg-muted/50 transition-colors",
                                    variant.disabled === 1 && "opacity-60",
                                  )}
                                  onClick={() => navigate(variantRoute)}
                                  aria-label={variant.disabled === 1 ? `${variant.item_code}, archived` : undefined}
                                >
                                  {visibleColumnFieldnames.map(fieldname => (
                                    <TableCell key={fieldname}>
                                      {renderCell(fieldname, variant)}
                                    </TableCell>
                                  ))}
                                  <TableCell className="w-[100px]">
                                    <VariantRowActions
                                      variantName={variant.name}
                                      itemCode={variant.item_code}
                                      disabled={variant.disabled}
                                      templateItemCode={itemCode}
                                      showArchivedVariants={showArchivedVariants}
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </React.Fragment>
                        );
                      })
                    ) : (
                      // Flat table view
                      filteredVariants.map(variant => {
                        const variantRoute = buildDocRoute('Item', variant.item_code);
                        return (
                          <TableRow
                            key={variant.name}
                            className={cn(
                              "cursor-pointer hover:bg-muted/50 transition-colors",
                              variant.disabled === 1 && "opacity-60",
                            )}
                            onClick={() => navigate(variantRoute)}
                            aria-label={variant.disabled === 1 ? `${variant.item_code}, archived` : undefined}
                          >
                            {visibleColumnFieldnames.map(fieldname => (
                              <TableCell key={fieldname}>
                                {renderCell(fieldname, variant)}
                              </TableCell>
                            ))}
                            <TableCell className="w-[100px]">
                              <VariantRowActions
                                variantName={variant.name}
                                itemCode={variant.item_code}
                                disabled={variant.disabled}
                                templateItemCode={itemCode}
                                showArchivedVariants={showArchivedVariants}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Page {pageIndex + 1} of {totalPages}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageIndex((prev) => Math.max(0, prev - 1))}
                      disabled={pageIndex === 0 || isLoading}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageIndex((prev) => Math.min(totalPages - 1, prev + 1))}
                      disabled={pageIndex >= totalPages - 1 || isLoading}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </CardContent>
      
      {/* List Settings Modal */}
      {listSettings && (
        <ListSettingsModal
          open={isListSettingsModalOpen}
          onOpenChange={setIsListSettingsModalOpen}
          fields={listSettings.availableFields}
          visibleFieldOrder={listSettings.visibleFieldOrder}
          onChangeVisibleFieldOrder={listSettings.setVisibleFieldOrder}
          onToggleField={listSettings.toggleField}
          isLoading={listSettings.isLoading}
        />
      )}
    </Card>
  );
}
